/**
 * TranscriptsTab — the "Transcripts & Documents" view in the Grounded
 * Emergence results.
 *
 * Lets the user pick one of the items the analysis was actually run on
 * (the IDs come straight from analysisItem.qualitative_items_included),
 * then renders that item's turns / paragraphs with the codes the
 * analysis applied to each one shown as small chips below the text.
 *
 * Hovering a chip highlights every span of that turn / paragraph that
 * the analysis attributed to that code; clicking a chip opens the
 * existing CodeDetailsModal so the user can jump from "this turn was
 * coded X" to "where else does X appear, with its full definition".
 *
 * All data is already fetched on the client — codes_quotes_dict via the
 * same query CodesTab uses (cache shared), and each item's body via the
 * existing transcript-detail endpoint (works identically for documents,
 * which now live in Qualitative_Items). No backend changes.
 */
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { fetchCodeQuotes, fetchTranscriptDetails } from '@services/interview';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import AddCodeToTurnPopover from '@components/interview/code-review/AddCodeToTurnPopover';
import TurnCodeChip from '@components/interview/code-review/TurnCodeChip';
import CodeDetailsModal from './CodeDetailsModal';

/**
 * Coerce a `qualitative_items_included` entry into a plain string ID.
 *
 * The Pydantic model declares `List[str]`, but in practice we've seen
 * older / mongo-flavoured documents come through as objects shaped
 * `{ id }`, `{ _id }`, or `{ $oid }`. Without this normalisation those
 * objects were being interpolated straight into the request URL as
 * `[object Object]`, which 500'd the transcript-detail endpoint.
 */
function normalizeItemId(entry) {
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object') {
    if (typeof entry.id === 'string') return entry.id;
    if (typeof entry._id === 'string') return entry._id;
    if (typeof entry.$oid === 'string') return entry.$oid;
    if (typeof entry._id === 'object' && typeof entry._id?.$oid === 'string') {
      return entry._id.$oid;
    }
  }
  return null;
}

/**
 * Walk codes_quotes_dict and build a per-quote-location index:
 *   key   = `${itemId}:t:${turnId}`   (transcripts)
 *         | `${itemId}:p:${paragraphId}` (documents)
 *   value = array of { codeName, definition, text, location }
 *
 * One walk over the whole dict, O(N). Memoised in the caller so we
 * don't rebuild this on every render or hover. Same source can appear
 * under different codes — that's a feature; each becomes its own entry.
 */
function buildTurnCodesIndex(codesQuotesDict) {
  const idx = new Map();
  if (!codesQuotesDict) return idx;
  for (const [codeName, entry] of Object.entries(codesQuotesDict)) {
    // codes_quotes_dict entries are usually objects with .sources, but
    // an older shape exposed a bare array — handle both defensively.
    const sources = Array.isArray(entry) ? entry : entry?.sources || [];
    const definition = Array.isArray(entry) ? '' : entry?.definition || '';
    for (const src of sources) {
      const itemId = src?.transcript_id || src?.document_id;
      if (!itemId) continue;
      const isDoc = !!src.document_id;
      const quoteId = isDoc ? src.paragraph_id : src.turn_id;
      if (quoteId === undefined || quoteId === null) continue;
      const key = `${itemId}:${isDoc ? 'p' : 't'}:${quoteId}`;
      if (!idx.has(key)) idx.set(key, []);
      idx.get(key).push({
        codeName,
        definition,
        text: typeof src.text === 'string' ? src.text : '',
        location: typeof src.location === 'number' ? src.location : null,
      });
    }
  }
  return idx;
}

/**
 * Find every occurrence of `snippet` inside `fullText`. We use
 * indexOf-based scanning rather than the raw `location` field because
 * `location` is set by the worker during open coding and isn't always
 * a precise character index for every code source. Text matching is
 * reliable and lets multiple sources of the same code highlight
 * independent occurrences in one turn.
 */
function findOccurrences(fullText, snippet) {
  if (!fullText || !snippet) return [];
  const needle = snippet.trim();
  if (!needle) return [];
  const occurrences = [];
  let cursor = 0;
  while (cursor <= fullText.length - needle.length) {
    const found = fullText.indexOf(needle, cursor);
    if (found === -1) break;
    occurrences.push({ start: found, end: found + needle.length });
    cursor = found + needle.length;
  }
  return occurrences;
}

/**
 * Turn the text + a list of [start,end) ranges into a list of
 * React nodes — plain runs interleaved with <mark>-style highlights.
 * Ranges are merged when they touch or overlap so we never emit
 * adjacent <mark> elements (would render as two boxes).
 */
function renderTextWithHighlights(text, ranges) {
  if (!ranges || ranges.length === 0) return text;
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged = [];
  for (const r of sorted) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) {
      last.end = Math.max(last.end, r.end);
    } else {
      merged.push({ start: r.start, end: r.end });
    }
  }
  const nodes = [];
  let cursor = 0;
  merged.forEach((r, i) => {
    if (r.start > cursor) {
      nodes.push(<span key={`p-${i}`}>{text.slice(cursor, r.start)}</span>);
    }
    nodes.push(
      <Box
        component="mark"
        key={`h-${i}`}
        sx={{
          bgcolor: '#fff7c2',
          color: 'inherit',
          px: 0.25,
          borderRadius: 0.5,
        }}
      >
        {text.slice(r.start, r.end)}
      </Box>,
    );
    cursor = r.end;
  });
  if (cursor < text.length) {
    nodes.push(<span key="tail">{text.slice(cursor)}</span>);
  }
  return nodes;
}

/**
 * Compact speaker chip — single Chip with a coloured letter avatar (I/P)
 * instead of two side-by-side chips. Avoids the "interviewer interviewer"
 * duplication that appeared when a speaker's name was literally the same
 * word as their role; collapses to one chip in that case.
 *
 * The avatar carries the role identity; the label carries the speaker's
 * distinguishing name when there is one. A tooltip surfaces the full
 * role word ("Interviewer" / "Participant") on hover for accessibility.
 */
const SpeakerChip = ({ speaker, role }) => {
  const speakerName = typeof speaker?.name === 'string' ? speaker.name.trim() : '';
  const isInterviewer = role === 'interviewer';
  const isParticipant = role === 'participant';
  const letter = isInterviewer ? 'I' : isParticipant ? 'P' : null;
  const roleLabel = isInterviewer ? 'Interviewer' : isParticipant ? 'Participant' : '';
  // Hide the name when it matches the role word — that's the duplication
  // case ("interviewer / interviewer"). The avatar already conveys role,
  // so we fall back to the human-readable role label as the chip text.
  const showName = !!speakerName && speakerName.toLowerCase() !== (role || '').toLowerCase();
  const label = showName ? speakerName : roleLabel || speakerName || 'Speaker';
  const tooltip =
    showName && roleLabel ? `${roleLabel} — ${speakerName}` : roleLabel || speakerName;

  if (!letter) {
    if (!speakerName) return null;
    return <Chip size="small" label={speakerName} variant="outlined" />;
  }

  return (
    <Tooltip title={tooltip} arrow>
      <Chip
        size="small"
        variant="outlined"
        label={label}
        avatar={
          <Avatar
            sx={{
              bgcolor: isInterviewer ? 'primary.main' : 'grey.600',
              color: 'common.white',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {letter}
          </Avatar>
        }
      />
    </Tooltip>
  );
};

const TurnBlock = ({
  isDoc,
  paragraphNumber,
  speaker,
  role,
  text,
  codes,
  onCodeClick,
  // Review-mode props — only set when the parent analysis_item is in
  // paused_for_review. When provided, each chip becomes a TurnCodeChip
  // (click for unassign / rename / delete menu) and an "Add code"
  // popover appears under the chip row (autocomplete of existing codes
  // + fallback to create-new-with-definition).
  isPausedForReview = false,
  studyId,
  analysisItemId,
  qualitativeItemId,
  turnId,
  codesQuotesDict,
}) => {
  const [hoveredCode, setHoveredCode] = useState(null);

  // Deduplicate codes by name for the chip row. A single turn can be
  // coded with the same name at multiple locations; we still want one
  // chip — hovering it lights up *all* of its occurrences in the text.
  const uniqueCodes = useMemo(() => {
    const m = new Map();
    for (const c of codes) {
      if (!m.has(c.codeName)) m.set(c.codeName, c);
    }
    return Array.from(m.values());
  }, [codes]);

  const highlightRanges = useMemo(() => {
    if (!hoveredCode) return [];
    return codes
      .filter((c) => c.codeName === hoveredCode)
      .flatMap((c) => findOccurrences(text, c.text));
  }, [hoveredCode, codes, text]);

  const hasCodes = uniqueCodes.length > 0;
  // Interview transcripts only get codes on participant turns — the
  // interviewer is asking questions, not contributing data to analyse,
  // so don't surface the Add code affordance there. Document paragraphs
  // have no role and always allow tagging.
  const canAddCode =
    isPausedForReview &&
    studyId &&
    qualitativeItemId &&
    turnId !== undefined &&
    role !== 'interviewer';

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5} useFlexGap flexWrap="wrap">
        {isDoc ? (
          <Chip size="small" label={`¶ ${paragraphNumber}`} sx={{ fontFamily: 'monospace' }} />
        ) : (
          <SpeakerChip speaker={speaker} role={role} />
        )}
      </Stack>

      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          lineHeight: 1.65,
          mb: hasCodes || canAddCode ? 2 : 0,
        }}
      >
        {renderTextWithHighlights(text, highlightRanges)}
      </Typography>

      {hasCodes || canAddCode ? (
        // Tinted container around the codes section so the analyst's
        // data (open codes + Add code) is visually separated from the
        // speaker / role metadata above. Same brand-purple as the
        // interviewer chip but at low alpha so it reads as a panel,
        // not an alert.
        <Box
          sx={(theme) => ({
            p: 1.5,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.12),
          })}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: 'text.secondary',
              mb: hasCodes ? 1 : 0.5,
              fontSize: 10,
            }}
          >
            Codes
          </Typography>

          {hasCodes ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              {uniqueCodes.map((c) => {
                const isActive = hoveredCode === c.codeName;
                const hoverHandlers = {
                  onMouseEnter: () => setHoveredCode(c.codeName),
                  onMouseLeave: () => setHoveredCode((prev) => (prev === c.codeName ? null : prev)),
                  onFocus: () => setHoveredCode(c.codeName),
                  onBlur: () => setHoveredCode((prev) => (prev === c.codeName ? null : prev)),
                };
                // In review mode, swap the read-only Chip for a
                // TurnCodeChip that opens a menu (Unassign from this
                // passage / Rename / Delete from analysis). The
                // hover-highlight behaviour is preserved by passing the
                // same mouse handlers through.
                if (isPausedForReview && studyId && qualitativeItemId && turnId !== undefined) {
                  return (
                    <TurnCodeChip
                      key={c.codeName}
                      studyId={studyId}
                      analysisItemId={analysisItemId}
                      qualitativeItemId={qualitativeItemId}
                      turnId={turnId}
                      codeName={c.codeName}
                      definition={c.definition}
                      isActive={isActive}
                      onCodeClick={onCodeClick}
                      {...hoverHandlers}
                    />
                  );
                }
                return (
                  <Chip
                    key={c.codeName}
                    size="small"
                    label={c.codeName}
                    variant={isActive ? 'filled' : 'outlined'}
                    color={isActive ? 'primary' : 'default'}
                    clickable
                    onClick={() => onCodeClick({ name: c.codeName, definition: c.definition })}
                    {...hoverHandlers}
                    sx={{ cursor: 'pointer', bgcolor: 'background.paper' }}
                  />
                );
              })}
            </Stack>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}
            >
              No codes assigned to this passage yet.
            </Typography>
          )}

          {canAddCode ? (
            <Box sx={{ mt: hasCodes ? 0.5 : 0 }}>
              <AddCodeToTurnPopover
                studyId={studyId}
                analysisItemId={analysisItemId}
                qualitativeItemId={qualitativeItemId}
                turnId={turnId}
                highlightedText={text || ''}
                codesQuotesDict={codesQuotesDict}
              />
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

const ItemBody = ({
  itemData,
  itemId,
  turnCodesIndex,
  onCodeClick,
  isPausedForReview = false,
  studyId,
  analysisItemId,
  codesQuotesDict,
}) => {
  const isDoc = (itemData?.item_type || 'transcript') === 'document';
  const transcript = Array.isArray(itemData?.transcript) ? itemData.transcript : [];
  const speakers = Array.isArray(itemData?.speakers) ? itemData.speakers : [];

  if (transcript.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          This item has no content to display.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {transcript.map((turn, idx) => {
        // Documents store paragraph_id as the entry's id (1-indexed integer
        // from the customer-api upload). Transcripts store turn_id as the
        // entry's id. Either way we read turn.id; falling back to the
        // array index for the rare older row missing id keeps the visual
        // intact even if codes won't match for it.
        const quoteId = typeof turn.id === 'number' || typeof turn.id === 'string' ? turn.id : idx;
        const key = `${itemId}:${isDoc ? 'p' : 't'}:${quoteId}`;
        const codesForTurn = turnCodesIndex.get(key) || [];
        const speaker = !isDoc ? speakers.find((s) => s?.id === turn.speaker_id) : null;
        const role = speaker?.role || null;
        return (
          <TurnBlock
            key={`${key}-${idx}`}
            isDoc={isDoc}
            paragraphNumber={isDoc ? idx + 1 : null}
            speaker={speaker}
            role={role}
            text={typeof turn.text === 'string' ? turn.text : ''}
            codes={codesForTurn}
            onCodeClick={onCodeClick}
            isPausedForReview={isPausedForReview}
            studyId={studyId}
            analysisItemId={analysisItemId}
            qualitativeItemId={itemId}
            turnId={quoteId}
            codesQuotesDict={codesQuotesDict}
          />
        );
      })}
    </Stack>
  );
};

const TranscriptsTab = ({ item, isPausedForReview = false }) => {
  const { id: studyId } = useParams();
  const analysisItemId = item?.analysis_item_id;

  const itemIds = useMemo(
    () =>
      Array.isArray(item?.qualitative_items_included)
        ? item.qualitative_items_included.map(normalizeItemId).filter(Boolean)
        : [],
    [item],
  );

  const [selectedItemId, setSelectedItemId] = useState(() => itemIds[0] || null);

  // Re-anchor the selection if the analysis item changes (different
  // analysis opened) or the included-items list updates — without this,
  // we'd hold onto a stale ID and the dropdown would render empty.
  useEffect(() => {
    if (itemIds.length === 0) {
      setSelectedItemId(null);
      return;
    }
    if (!selectedItemId || !itemIds.includes(selectedItemId)) {
      setSelectedItemId(itemIds[0]);
    }
  }, [itemIds, selectedItemId]);

  // Same query key the CodesTab uses → cache is shared. Opening this
  // tab after Codes is instant; opening it first warms the cache for
  // Codes too.
  const { data: codeQuotes } = useQuery({
    queryKey: ['interview-analysis-code', studyId],
    queryFn: () => fetchCodeQuotes({ studyId }),
    enabled: !!studyId,
  });

  // Fetch every included item in parallel so the dropdown can show real
  // names from the start — without this the dropdown rendered "Item 1",
  // "Item 2", etc. on first open and only filled in real names as the
  // user clicked through each one (an earlier optimisation that prized
  // O(1) network on mount but produced an actively confusing label
  // sequence in the review UX). Same query key as TranscriptDetail uses
  // so cache stays shared; staleTime keeps these warm for the session.
  const itemQueries = useQueries({
    queries: itemIds.map((iid) => ({
      queryKey: ['transcript', iid, studyId],
      queryFn: () => fetchTranscriptDetails({ studyId, transcriptId: iid }),
      enabled: !!studyId && !!iid,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const itemsById = useMemo(() => {
    const m = new Map();
    itemIds.forEach((iid, idx) => {
      const data = itemQueries[idx]?.data;
      if (data) m.set(iid, data);
    });
    return m;
  }, [itemIds, itemQueries]);

  const selectedIdx = itemIds.indexOf(selectedItemId);
  const selectedQuery = selectedIdx >= 0 ? itemQueries[selectedIdx] : null;
  const selectedItem = selectedQuery?.data;
  const isSelectedLoading = !!selectedItemId && !selectedItem && (selectedQuery?.isLoading ?? true);

  const turnCodesIndex = useMemo(
    () => buildTurnCodesIndex(codeQuotes?.codes_quotes_dict),
    [codeQuotes],
  );

  const [openCode, setOpenCode] = useState(null);

  if (itemIds.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          This analysis didn&rsquo;t record any included items.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Transcripts & Documents
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Each turn or paragraph shows the codes the analysis applied. Hover a code to highlight
            its text; click to see every place it was used.
          </Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 320 } }}>
          <InputLabel id="transcripts-tab-item-select-label">Item</InputLabel>
          <Select
            labelId="transcripts-tab-item-select-label"
            label="Item"
            value={selectedItemId || ''}
            onChange={(e) => setSelectedItemId(e.target.value)}
          >
            {itemIds.map((iid) => {
              // All items fetch in parallel on mount, so `data` arrives
              // within one round-trip and the dropdown shows real names
              // from the first open. Until the response lands we render
              // a small spinner instead of a placeholder name, so the
              // user never sees a misleading "Item 1" label.
              const data = itemsById.get(iid);
              const known = !!data;
              const isDoc = known && data?.item_type === 'document';
              return (
                <MenuItem key={iid} value={iid}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {known ? (
                      isDoc ? (
                        <ArticleOutlinedIcon fontSize="small" color="action" />
                      ) : (
                        <ForumOutlinedIcon fontSize="small" color="action" />
                      )
                    ) : (
                      <CircularProgress size={14} />
                    )}
                    <Box component="span" sx={{ minWidth: 0 }}>
                      {known ? data?.name || 'Untitled' : 'Loading…'}
                    </Box>
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Stack>

      {isSelectedLoading || !selectedItem ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <ItemBody
          itemData={selectedItem}
          itemId={selectedItemId}
          turnCodesIndex={turnCodesIndex}
          onCodeClick={(c) => setOpenCode(c)}
          isPausedForReview={isPausedForReview}
          studyId={studyId}
          analysisItemId={analysisItemId}
          codesQuotesDict={codeQuotes?.codes_quotes_dict}
        />
      )}

      <CodeDetailsModal
        open={Boolean(openCode)}
        onClose={() => setOpenCode(null)}
        code={openCode}
        codesQuotesDict={codeQuotes?.codes_quotes_dict}
      />
    </Box>
  );
};

export default TranscriptsTab;
